/**
 * AIFleetInsightsPage.tsx
 *
 * Cross-cutting fleet intelligence — synthesizes real, already-computed
 * data from across the whole diesel system (alerts, reconciliation
 * completeness, budget planning variances, pending approvals, active
 * technicians) into one prioritized briefing, rather than requiring a
 * manager to check five separate pages to build the same picture
 * themselves. Works with or without a Gemini API key configured — the
 * underlying evidence is always shown; the AI narrative on top of it is
 * a bonus, not the whole value.
 */
import React, { useEffect, useState } from 'react';
import { useCycles } from '../../hooks/useDiesel';
import { aiInsightsApi } from '../../api/dieselApi';
import { PageHeader, Spinner, ErrorBox, Btn, CycleSelector, KpiCard, Tag, type TagVariant } from '../../components/diesel/DieselComponents';
import { neutral, ink, urgency, font } from '../../styles/dieselTokens';

const CONFIDENCE_VARIANT: Record<string, TagVariant> = { high: 'success', medium: 'warning', low: 'danger' };

// Renders Gemini's "**bold**" spans as actual bold text instead of showing
// the literal asterisks — the narrative leans on inline bold heavily (site
// IDs, percentages, counts) alongside the "## Heading" block markdown, so
// both need handling even though this stays well short of a full markdown
// library.
function renderInline(line: string, keyPrefix: React.Key) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${keyPrefix}-${j}`} style={{ fontWeight: 700, color: ink }}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-${j}`}>{part}</React.Fragment>;
  });
}

function renderNarrative(text: string) {
  // Gemini's response uses simple "## Heading" markdown for section titles
  // and "**bold**" inline — rendered here directly rather than pulling in
  // a full markdown library for what's really three headings plus bold
  // spans.
  return text.split('\n').map((line, i) => {
    if (line.startsWith('## ')) {
      return <div key={i} style={{ fontSize: 14, fontWeight: 700, color: ink, marginTop: i === 0 ? 0 : 16, marginBottom: 6 }}>{renderInline(line.replace('## ', ''), i)}</div>;
    }
    if (!line.trim()) return null;
    return <div key={i} style={{ fontSize: 13, color: neutral[700], lineHeight: 1.6, marginBottom: 4 }}>{renderInline(line, i)}</div>;
  });
}

export default function AIFleetInsightsPage() {
  const { cycles, selectedKey, setSelectedKey } = useCycles();
  const [briefing, setBriefing] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNarrative, setShowNarrative] = useState(true);

  async function loadBriefing(refresh = false) {
    if (!selectedKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await aiInsightsApi.briefing(selectedKey, refresh);
      setBriefing((res as any)?.data || res);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load briefing');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (selectedKey) loadBriefing(); }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const ev = briefing?.evidence;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto', fontFamily: font.ui }}>
      <PageHeader
        title="Fleet Intelligence Briefing"
        subtitle="One prioritized picture of the whole fleet — alerts, budget variances, reconciliation gaps, and pending approvals — synthesized, not scattered across five pages."
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <CycleSelector cycles={cycles} value={selectedKey} onChange={setSelectedKey} />
            <Btn variant="secondary" size="sm" onClick={() => loadBriefing(true)} loading={loading}>↻ Refresh</Btn>
          </div>
        }
      />

      {error && <ErrorBox message={error} onRetry={() => loadBriefing()} />}

      {loading && !briefing && <Spinner />}

      {briefing && (
        <>
          {briefing.narrative && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: neutral[600], cursor: 'pointer' }}>
                <input type="checkbox" checked={showNarrative} onChange={e => setShowNarrative(e.target.checked)} /> Show AI narrative
              </label>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: neutral[400] }}>
                Generated {new Date(briefing.generatedAt).toLocaleString()}
                {briefing.cached && ' · cached — click Refresh for the latest'}
              </span>
            </div>
          )}

          {!briefing.ai_enabled && (
            <div style={{ background: neutral[50], border: `1px solid ${neutral[200]}`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: neutral[600] }}>
              Gemini isn't configured (set <code>GEMINI_API_KEY</code> in your backend's .env) — showing the raw evidence below without an AI-written narrative on top. Everything the narrative would summarize is still here.
            </div>
          )}

          {briefing.narrative && showNarrative && (
            <div style={{ border: `1px solid ${neutral[200]}`, borderRadius: 12, padding: 20, marginBottom: 20, background: '#fff' }}>
              {renderNarrative(briefing.narrative)}
            </div>
          )}

          {ev && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <KpiCard label="Open Alerts" value={ev.alerts?.total_open ?? 0} tone={(ev.alerts?.total_open ?? 0) > 0 ? 'crit' : 'ok'} />
              <KpiCard label="Sites Missing Field Data" value={ev.reconciliation?.missing_field_data ?? 0} tone={(ev.reconciliation?.missing_field_data ?? 0) > 0 ? 'warn' : 'ok'} />
              <KpiCard label="Budget Plan Flags" value={ev.budget_planning?.total_flagged ?? 0} tone={(ev.budget_planning?.total_flagged ?? 0) > 0 ? 'warn' : 'ok'} />
              <KpiCard label="Technicians Active Now" value={ev.active_technicians_now ?? 0} tone="ok" />
            </div>
          )}

          {ev?.budget_planning?.top_flagged_sites?.length > 0 && (
            <div style={{ border: `1px solid ${neutral[200]}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Sites With the Largest Formula-vs-Reality Gap</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Site', 'Variance', 'Confidence'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${neutral[200]}`, color: neutral[500] }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ev.budget_planning.top_flagged_sites.map((s: any) => (
                    <tr key={s.site_id} style={{ borderBottom: `1px solid ${neutral[100]}` }}>
                      <td style={{ padding: '6px 8px' }}>{s.site_name || s.site_id}</td>
                      <td style={{ padding: '6px 8px', color: Math.abs(s.variance_pct) > 40 ? urgency.critical.fg : urgency.high.fg, fontWeight: 700 }}>
                        {s.variance_pct > 0 ? '+' : ''}{s.variance_pct}%
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <Tag variant={CONFIDENCE_VARIANT[String(s.confidence).toLowerCase()] || 'neutral'}>
                          {s.confidence}
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11.5, color: neutral[400], marginTop: 8 }}>
                Confidence here is the budget formula's own track record at a site (more confirmed cycles behind it = higher) — a different dimension from the data-freshness confidence gate on Fuel Planning.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
